import React, { useState } from 'react';
import { X, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { WithdrawalRequest } from '../../../types';
import { useRejectWithdrawal, useResolveFraudFlag, useFraudFlags } from '../../../hooks/useApi';
import { getWithdrawalListLabel } from '../../../utils/withdrawalRequester';
import { LoadingSpinner } from '../../ui/Loading';
import { formatWithdrawalDisplayAmount } from '../../../utils/portalUsdWithdrawalDisplay';
interface RejectModalProps {
  withdrawal: WithdrawalRequest | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const parseErrorMessage = (error: any): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.message) {
    return error.message;
  }
  return 'An error occurred while rejecting withdrawal';
};

const RejectModal: React.FC<RejectModalProps> = ({
  withdrawal,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [reason, setReason] = useState('');
  const rejectWithdrawalMutation = useRejectWithdrawal();
  const resolveFraudFlagMutation = useResolveFraudFlag();

  // Fetch associated fraud flags for this withdrawal
  const { data: fraudFlagsData } = useFraudFlags(
    withdrawal
      ? { transactionId: withdrawal.transactionId, isResolved: false }
      : undefined
  );

  const unresolvedFlags = fraudFlagsData?.data?.fraudFlags || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!withdrawal) return;

    if (!reason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    try {
      // 1. Reject the withdrawal
      await rejectWithdrawalMutation.mutateAsync({
        transactionId: withdrawal.transactionId,
        data: {
          reason: reason.trim(),
        },
      });

      // 2. Resolve all associated fraud flags
      for (const flag of unresolvedFlags) {
        try {
          await resolveFraudFlagMutation.mutateAsync({
            flagId: flag.id,
            data: {
              notes: `Rejected - ${reason.trim()}`,
            },
          });
        } catch (err) {
          console.warn('Failed to resolve fraud flag:', flag.id, err);
        }
      }

      toast.success('Withdrawal rejected successfully');
      onSuccess?.();
      onClose();
      setReason('');
    } catch (error: any) {
      console.error('Reject withdrawal error:', error);
      toast.error(parseErrorMessage(error));
    }
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  if (!isOpen || !withdrawal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg font-semibold">Reject Flagged Withdrawal</CardTitle>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to reject this flagged withdrawal? Funds will be returned
                to the user's wallet. All associated fraud flags will be resolved.
              </p>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm">
                  <div className="font-medium font-mono text-xs">{withdrawal.transactionId}</div>
                  <div className="text-muted-foreground">{getWithdrawalListLabel(withdrawal)}</div>
                  <div className="font-medium text-red-600">
                    {formatWithdrawalDisplayAmount(withdrawal, { signed: true })}
                  </div>
                </div>
              </div>
              {unresolvedFlags.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 p-3 rounded-lg">
                  <p className="text-xs font-medium text-orange-800">
                    {unresolvedFlags.length} unresolved fraud flag(s) will be marked as resolved.
                  </p>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="reason" className="block text-sm font-medium mb-2">
                Rejection Reason <span className="text-red-600">*</span>
              </label>
              <Input
                id="reason"
                type="text"
                placeholder="e.g., Suspected exploit. Profit ratio abnormally high..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={1000}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                {reason.length}/1000 characters
              </p>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={rejectWithdrawalMutation.isLoading}
              >
                {rejectWithdrawalMutation.isLoading ? (
                  <div className="flex items-center">
                    <LoadingSpinner />
                    <span className="ml-2">Rejecting...</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject Withdrawal
                  </div>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default RejectModal;
